/**
 * Browser-local recent-activity feed for anonymous users.
 *
 * Signed-in users get their feed from /api/history; anon users have
 * no server-side row, so we keep a small rolling list in localStorage
 * keyed on this single key. Last 5 items, prepended on each successful
 * /analyze, /calculate, or /compare submit.
 *
 * Items are stored in a denormalised shape that matches what the
 * RecentActivity card needs to render - we don't persist full
 * AnalysisResult/SellResult/CompareResponse blobs (they'd blow past
 * a few KB per row). A click on an analyze row replays via
 * /analyze?url= (the full result is already cached separately under
 * ANON_RESULT_STORAGE_KEY for the most-recent one). Calculate and
 * compare anon items are display-only - they have no permalink, so
 * the card just shows the snapshot.
 */
import type { AnalysisResult, SellResult, CompareResponse } from "@/types";

const STORAGE_KEY = "menzil-anon-recent";
const MAX_ITEMS = 5;
// 7 days. Long enough that a returning user sees their last week of
// activity, short enough that we don't carry forever-stale entries
// across browser updates / abandoned sessions.
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

export type RecentKind = "analyze" | "calculate" | "compare";

export interface RecentActivityItem {
  /** Stable key for React. Generated client-side; never collides with
   *  server history row ids since signed-in users don't read this list. */
  id: string;
  kind: RecentKind;
  ts: number;
  /** Sale vs rent label for badge rendering. Compare rows inherit
   *  whatever the first listing was. */
  mode?: "sale" | "rent" | null;
  /** Analyze + compare only. */
  url?: string | null;
  /** Verdict label (English, untranslated). The renderer routes it
   *  through translateVerdict() at display time. */
  verdict?: string | null;
  price?: number | null;
  predicted_price?: number | null;
  overpriced_pct?: number | null;
  location_name?: string | null;
  room_count?: number | null;
  area_m2?: number | null;
  /** Compare only - number of listings in the run, plus a short
   *  district preview for the row caption. */
  listing_count?: number | null;
  compare_preview?: string | null;
}

interface PersistedFeed {
  items: RecentActivityItem[];
}

function makeId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `r_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function getRecentActivity(): RecentActivityItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as PersistedFeed;
    if (!parsed?.items || !Array.isArray(parsed.items)) return [];
    const cutoff = Date.now() - TTL_MS;
    return parsed.items.filter((it) => typeof it.ts === "number" && it.ts >= cutoff);
  } catch {
    return [];
  }
}

function saveItems(items: RecentActivityItem[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ items } satisfies PersistedFeed));
  } catch {
    /* quota / disabled storage - non-fatal */
  }
}

/** Prepend a new entry, dedupe (analyze rows by URL, calculate by
 *  area+rooms+location, compare by URL list), and cap at MAX_ITEMS. */
export function addRecentActivity(item: Omit<RecentActivityItem, "id" | "ts">) {
  if (typeof window === "undefined") return;
  const existing = getRecentActivity();
  const next: RecentActivityItem = { ...item, id: makeId(), ts: Date.now() };

  const deduped = existing.filter((prev) => {
    if (prev.kind !== next.kind) return true;
    if (next.kind === "analyze") {
      // Same URL = same listing, drop the older row.
      return !(prev.url && next.url && prev.url === next.url);
    }
    if (next.kind === "compare") {
      // Compare dedup is best-effort: same listing_count + same
      // first 60 chars of preview is "probably the same compare run"
      // and we don't want a refresh to push a duplicate. The full
      // URL list isn't on the row by design (compare rows can be
      // 4 listings, too long for the snapshot).
      return !(
        prev.listing_count === next.listing_count &&
        (prev.compare_preview ?? "") === (next.compare_preview ?? "")
      );
    }
    // Calculate: dedup on the location + area + rooms triple.
    return !(
      prev.location_name === next.location_name &&
      prev.area_m2 === next.area_m2 &&
      prev.room_count === next.room_count
    );
  });

  const trimmed = [next, ...deduped].slice(0, MAX_ITEMS);
  saveItems(trimmed);
}

/* ── Convenience adapters - call these from the submit handlers ── */

export function recordAnalyze(result: AnalysisResult, fallbackUrl: string | null = null) {
  addRecentActivity({
    kind: "analyze",
    mode: result.analysis_mode ?? "sale",
    url: result.url || fallbackUrl,
    verdict: result.verdict ?? null,
    price: result.price ?? null,
    predicted_price: result.predicted_price ?? null,
    overpriced_pct: result.overpriced_pct ?? null,
    location_name: result.location_name ?? null,
    room_count: result.room_count ?? null,
    area_m2: result.area_m2 ?? null,
  });
}

export function recordCalculate(result: SellResult) {
  addRecentActivity({
    kind: "calculate",
    mode: result.mode ?? "sale",
    predicted_price: result.predicted_price ?? null,
    location_name: result.location_name ?? null,
    room_count: result.room_count ?? null,
    area_m2: result.area_m2 ?? null,
  });
}

export function recordCompare(response: CompareResponse) {
  const results = response.results ?? [];
  if (results.length === 0) return;
  // Build a 2-3 district preview so the row caption shows something
  // meaningful in the list ("Yasamal · Nəsimi · Xətai").
  const districts = results
    .map((r) => r.location_name)
    .filter((n): n is string => !!n)
    .slice(0, 3);
  const preview = districts.length > 0
    ? Array.from(new Set(districts)).join(" · ")
    : null;
  addRecentActivity({
    kind: "compare",
    mode: results[0]?.analysis_mode ?? "sale",
    listing_count: results.length,
    compare_preview: preview,
  });
}
